import React, { useEffect, useRef, useState } from "react";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ImageList from "@mui/material/ImageList";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { ImageData } from "../../../../models/Image/Image";
import "../../../../styles/imageUpload.css";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import CustomTypography from "../form/CustomTypography";

type ImageUploadProps = {
  id?: string;
  existingImageList?: ImageData[];
  newImageList?: File[];
  deleteImage?: (image: ImageData) => void;
  removeImage?: (image: ImageData) => void;
  addImagesToBeUploaded?: (files: File[]) => void;
  isViewOnly?: boolean;
  size: "small" | "medium" | "large";
  multiple?: boolean;
  uploadText?: string;
  showImagesList: boolean;
  previewImage?: boolean;
  minHeight?: string;
  minWidth?: string;
  minInputWidth?: string;
  maxWidth?: string;
  disabled?: boolean;
  showDownloadIcon?: boolean;
  allowedFileTypes?: string;
  fileInfo?: boolean | string;
  uploadIcon?: React.ReactNode;
};

const ImageUpload = ({
  id,
  existingImageList,
  newImageList,
  isViewOnly,
  deleteImage,
  removeImage,
  addImagesToBeUploaded,
  size,
  multiple = true,
  uploadText,
  showImagesList = true,
  previewImage = false,
  minHeight = "200px",
  minWidth = "120px",
  minInputWidth = "120px",
  maxWidth = "",
  disabled = false,
  showDownloadIcon = true,
  allowedFileTypes = "image/*",
  fileInfo = false,
  uploadIcon,
}: ImageUploadProps) => {
  const [imageData, setImageData] = useState<ImageData[]>([]);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const theme = useTheme();

  const inputRef = useRef<any>(null);

  const handleViewImage = (img: string) => {
    setSelectedImage(img);
  };

  const handleClosePopup = () => {
    setSelectedImage(null);
  };

  useEffect(() => {
    if (inputRef.current) inputRef.current.value = "";

    let localImageList = existingImageList ? [...existingImageList!] : [];

    const newImageUrls: { img: string; title: string }[] = [];
    if (newImageList && newImageList.length !== 0)
      newImageList.forEach((file: File) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result && typeof reader.result === "string") {
            newImageUrls.push({
              img: reader.result,
              title: file.name,
            });
          }

          if (newImageList.length === newImageUrls.length) {
            localImageList.push(...newImageUrls);
            setImageData(localImageList);
          }
        };
        reader.readAsDataURL(file);
      });
    else {
      localImageList.push(...newImageUrls);
      setImageData(localImageList);
    }
  }, [existingImageList, newImageList]);

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    addImagesToBeUploaded!(files);
    event.target.value = "";
  };
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    const validFiles = files.filter((file) =>
      allowedFileTypes
        .split(",")
        .some(
          (type) =>
            file.type.match(type.trim()) || file.name.match(type.trim()),
        ),
    );
    if (validFiles?.length > 0) {
      addImagesToBeUploaded?.(validFiles);
    }
    event.dataTransfer.clearData();
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const useStyles = {
    imageContainer: {
      // width: "1200px",
      display: "flex",
      //  flexDirection: 'column',
      // display: flex;
      maxWidth: "100%",
      // height: imageData.length > 0 ? "300px" : "0",
      overflowX: imageData.length > 0 ? "scroll" : "hidden",
      gap: "30px !important",
      "@media (min-width: 1200px)": {
        width: "950px",
      },
      "@media (max-width: 1320px)": {
        width: "600px",
        overflowX: "scroll",
      },
      "@media (max-width: 1140px)": {
        width: "580px",
        gap: "15px !important",
      },
      "@media (max-width: 700px)": {
        display: "flex",
        flexDirection: "column",
        gap: "15px !important",
        width: "300px",
      },
      "@media (max-width: 990px)": {
        width: "380px",
        gap: "20px !important",
      },
    },
    uploadContainer: {
      padding: "1rem",
      marginBottom: "1rem",
      overflowX: "hidden",
      border: "2px dashed #ccc",
      borderRadius: "8px",
      textAlign: "center",
      backgroundColor: "#f9f9f9",
      cursor: "pointer",
      // overflowY: "scroll",
      "@media (max-width: 699px)": {
        height: "300px",
        overflow: "scroll",
        padding: "1rem",
        marginBottom: "1rem",
      },
    },
  };

  const useMainStyles = {
    small: {
      width: "300px",
      gap: "10px !important",
      display: "flex",
      flexDirection: "column",
    },
    medium: {
      width: "1200px",
      gap: "20px !important",
      display: "flex",
      imageContainer: {
        // width: "1200px",
        display: "flex",
        flexDirection: "column",
      },
    },
    large: {
      imageContainer: {
        width: "1200px",
        // display:"flex"
      },
    },
  };

  return (
    <>
      {previewImage ? (
        <Box
          sx={{
            ...useMainStyles[size],
            overflow: "hidden",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            height: minHeight,
            width: "100%",
            borderRadius: "8px",
            minWidth: minWidth,
            maxWidth: maxWidth,
          }}
        >
          {imageData.length === 0 ? (
            <Box
              className="image-upload-container"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                width: "100%",
                marginBottom: "0px",
              }}
            >
              <label
                htmlFor={id ? `raised-button-file-${id}` : "raised-button-file"}
                className="file-upload-label"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
                  cursor: "pointer",
                  minWidth: minInputWidth,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#E0E7FF";
                  e.currentTarget.style.borderColor = "#4F46E5";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9f9f9";
                  e.currentTarget.style.borderColor = "#ccc";
                }}
              >
                <input
                  ref={inputRef}
                  accept={allowedFileTypes}
                  id={id ? `raised-button-file-${id}` : "raised-button-file"}
                  type="file"
                  multiple={multiple}
                  onChange={handleFiles}
                  style={{ display: "none" }}
                  disabled={disabled}
                />
                {uploadIcon || (
                  <UploadFileIcon
                    style={{
                      height: "40px",
                      width: "40px",
                      color: "#212121",
                      backgroundColor: "#f2f0ff",
                      borderRadius: "50%",
                      padding: "8px",
                    }}
                  />
                )}
                <span className="upload-text">
                  {uploadText ? (
                    <span className="upload-text">{uploadText}</span>
                  ) : (
                    <span className="upload-text">
                      Drop your image here, or{" "}
                      <span style={{ color: "#4e8bff" }}>browse</span>
                    </span>
                  )}
                </span>
                {fileInfo && (
                  <CustomTypography
                    variant="body3"
                    color={theme.palette.customColors.greyTextColor}
                    sx={{ mt: 1 }}
                  >
                    {typeof fileInfo === "string"
                      ? fileInfo
                      : "PNG, JPG up to 10MB each"}
                  </CustomTypography>
                )}
              </label>
            </Box>
          ) : (
            <>
              <Box
                className="image-upload-container-2"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
                  background: "#f4f4f4",
                }}
              >
                {imageData.map((image, index) => (
                  <Box
                    key={index}
                    className="image-container-2"
                    sx={{
                      position: "relative",
                      padding: "12px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      width: "100%",
                      border: "1px solid grey",
                      borderRadius: "8px",
                      overflow: "hidden",
                      background: "#fff",
                    }}
                    onMouseEnter={() => setHoverIndex(index)}
                    onMouseLeave={() => setHoverIndex(null)}
                  >
                    <img
                      srcSet={image.img}
                      src={image.img}
                      alt={image.img}
                      loading="lazy"
                      style={{
                        objectFit: "contain",
                        maxWidth: "100%",
                        maxHeight: "100%",
                      }}
                    />
                    {hoverIndex === index && (
                      <Box
                        className="delete-icon-2"
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          backgroundColor: "#00000020",
                          display: "flex",
                          justifyContent: "center",
                          gap: "10px",
                          alignItems: "center",
                          opacity: 1,
                          transition: "opacity 0.3s ease-in-out",
                          zIndex: 1000,
                        }}
                      >
                        <VisibilityOutlinedIcon
                          sx={{
                            color: "#fff",
                            cursor: "pointer",
                            backgroundColor: "#00000090",
                            borderRadius: "50%",
                            padding: "4px",
                            height: 30,
                            width: 30,
                          }}
                          onClick={() => handleViewImage(image.img)}
                        />
                        {showDownloadIcon && (
                          <DownloadOutlinedIcon
                            sx={{
                              color: "#fff",
                              cursor: "pointer",
                              backgroundColor: "#00000090",
                              borderRadius: "50%",
                              padding: "4px",
                              height: 30,
                              width: 30,
                            }}
                            onClick={() => {
                              const link = document.createElement("a");
                              link.href = image.img;
                              link.download = image.title!;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                          />
                        )}

                        <DeleteOutlinedIcon
                          onClick={() => {
                            deleteImage!(image);
                            removeImage!(image);
                          }}
                          sx={{
                            color: "#fff",
                            cursor: "pointer",
                            backgroundColor: "#00000090",
                            borderRadius: "50%",
                            padding: "4px",
                            height: 30,
                            width: 30,
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
              <Dialog open={!!selectedImage} onClose={handleClosePopup}>
                <DialogTitle>
                  <IconButton
                    aria-label="close"
                    onClick={handleClosePopup}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <img
                    src={selectedImage!}
                    alt="Preview"
                    style={{ maxWidth: "100%", maxHeight: "100%" }}
                  />
                </DialogContent>
              </Dialog>
            </>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            ...useMainStyles[size],
          }}
        >
          {isViewOnly === true ? undefined : (
            <Box
              className="image-upload-container"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <label
                htmlFor={id ? `raised-button-file-${id}` : "raised-button-file"}
                className="file-upload-label"
                style={{
                  height: minHeight || "100%",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#F3E8FF";
                  e.currentTarget.style.borderColor = "#9333EA";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9f9f9";
                  e.currentTarget.style.borderColor = "#ccc";
                }}
              >
                <input
                  ref={inputRef}
                  accept={allowedFileTypes}
                  id={id ? `raised-button-file-${id}` : "raised-button-file"}
                  type="file"
                  multiple={multiple}
                  onChange={handleFiles}
                  disabled={disabled}
                />
                {uploadIcon || (
                  <UploadFileIcon
                    style={{
                      cursor: "pointer",
                      height: 40,
                      width: 40,
                      color: "#212121",
                      backgroundColor: "#f2f0ff",
                      borderRadius: "50%",
                      padding: 8,
                    }}
                  />
                )}
                <span className="upload-text">
                  {uploadText ? (
                    <span className="upload-text">{uploadText}</span>
                  ) : (
                    <span className="upload-text">
                      Drop your image here, or{" "}
                      <span style={{ color: "#4e8bff" }}>browse</span>
                    </span>
                  )}
                </span>
                {fileInfo && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1, fontSize: "0.75rem" }}
                  >
                    {typeof fileInfo === "string"
                      ? fileInfo
                      : "PNG, JPG up to 10MB each"}
                  </Typography>
                )}
              </label>
            </Box>
          )}
          {showImagesList ? (
            <Box>
              {imageData.length > 0 && (
                <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                  {imageData.length === 0 ? "Image" : "Image"}
                </Typography>
              )}
              <ImageList sx={useStyles.imageContainer} cols={4} rowHeight={200}>
                {imageData.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      height: 150,
                      position: "relative",
                      padding: "10px",
                    }}
                  >
                    <img
                      srcSet={`${item.img}`}
                      src={`${item.img}`}
                      alt={item.title}
                      loading="lazy"
                      style={{
                        objectFit: "contain",
                        width: 120,
                        height: 120,
                        background: "#f4f4f4",
                      }}
                    />
                    {!isViewOnly ? (
                      <div
                        className="delete-icon"
                        onClick={() =>
                          item.id ? deleteImage!(item) : removeImage!(item)
                        }
                      >
                        <HighlightOffIcon style={{ fill: "#e84d4ded" }} />
                      </div>
                    ) : undefined}
                  </div>
                ))}
              </ImageList>
            </Box>
          ) : (
            <></>
          )}
        </Box>
      )}
    </>
  );
};

export default ImageUpload;
